import { FC, useMemo, useState } from "react";
import { Alert, Badge, Button, Form, InputGroup } from "react-bootstrap";
import Loader from "src/components/common/loader/loader";
import ModalDelete from "src/components/common/ModalDelete";
import { ApprovedUrl } from "@models/approvedUrl.model";
import useOrganizationApprovedUrls from "../hooks/useOrganizationApprovedUrls";
import ApprovedUrlFormModal, { ApprovedUrlFormValues } from "./ApprovedUrlFormModal";
import MoveApprovedUrlModal from "./MoveApprovedUrlModal";

interface Props {
  organizationId: number;
}

type StatusFilter = "all" | "active" | "inactive";

const ICON_BUTTON = {
  width: 32, height: 32, padding: 0,
  display: "flex", alignItems: "center", justifyContent: "center",
} as const;

const OrganizationApprovedUrlsTab: FC<Props> = ({ organizationId }) => {
  const { urls, isLoading, isSaving, error, createUrl, updateUrl, moveUrl, deleteUrl } =
    useOrganizationApprovedUrls(organizationId);

  const [formModal, setFormModal] = useState<{ approvedUrl: ApprovedUrl | null } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ApprovedUrl | null>(null);
  const [moveTarget, setMoveTarget] = useState<ApprovedUrl | null>(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<StatusFilter>("all");

  // A row belongs to an ancestor, so it is listed for context but edited on that org.
  const isOwned = (row: ApprovedUrl) => row.organization_id === organizationId;

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return urls.filter((row) => {
      if (status === "active" && !row.is_active) return false;
      if (status === "inactive" && row.is_active) return false;
      if (!needle) return true;
      return row.title.toLowerCase().includes(needle) || row.url.toLowerCase().includes(needle);
    });
  }, [urls, search, status]);

  // Addresses used for instant duplicate feedback in the form — this org's own rows,
  // excluding the one being edited. The parent's rows may legitimately repeat.
  const otherUrls = (current: ApprovedUrl | null) =>
    urls
      .filter((row) => isOwned(row) && row.id !== current?.id)
      .map((row) => row.url.toLowerCase());

  const handleSave = async (values: ApprovedUrlFormValues) => {
    const payload = {
      url: values.url,
      title: values.title || values.url,
      description: values.description || null,
      is_active: values.is_active,
    };
    if (formModal?.approvedUrl) {
      await updateUrl(formModal.approvedUrl.id, payload);
    } else {
      await createUrl(payload);
    }
    setFormModal(null); // reached only on success; on error the modal stays open
  };

  const handleMove = async (destinationOrganizationId: number) => {
    if (!moveTarget) return;
    await moveUrl(moveTarget.id, destinationOrganizationId);
    setMoveTarget(null); // reached only on success; on error the modal stays open
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteUrl(deleteTarget.id);
    } finally {
      setDeleteTarget(null);
    }
  };

  if (isLoading && urls.length === 0) {
    return <Loader />;
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h5 className="mb-1">Approved URLs</h5>
          <p className="text-muted small mb-0">
            The destinations Ops may point ad traffic at for this organization.
          </p>
        </div>
        <Button variant="primary" size="sm" onClick={() => setFormModal({ approvedUrl: null })} disabled={isSaving}>
          <i className="bi bi-plus-lg me-1" />
          Add URL
        </Button>
      </div>

      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

      <div className="d-flex flex-wrap gap-2 mb-3">
        <InputGroup size="sm" style={{ maxWidth: 320 }}>
          <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
          <Form.Control
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or address"
            aria-label="Search approved URLs"
          />
        </InputGroup>
        <Form.Select
          size="sm"
          style={{ maxWidth: 150 }}
          value={status}
          onChange={(e) => setStatus(e.target.value as StatusFilter)}
          aria-label="Filter by status"
        >
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </Form.Select>
      </div>

      {urls.length === 0 ? (
        <div className="text-muted text-center py-5">
          No approved URLs yet. Click <span className="fw-semibold">Add URL</span> to define one.
        </div>
      ) : visible.length === 0 ? (
        <div className="text-muted text-center py-5">No URLs match that search.</div>
      ) : (
        <div className="border rounded">
          <table className="table table-hover mb-0">
            <thead className="table-light">
              <tr>
                <th className="small fw-semibold">Name</th>
                <th className="small fw-semibold">Address</th>
                <th className="small fw-semibold" style={{ width: 160 }}>Owner</th>
                <th className="small fw-semibold" style={{ width: 130 }}></th>
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => (
                <tr key={row.id}>
                  <td className="small align-middle">
                    <div className="d-flex align-items-center gap-2">
                      <span className="fw-semibold">{row.title}</span>
                      {!row.is_active && (
                        <Badge bg="light" text="dark" className="border px-2 fw-normal">
                          Inactive
                        </Badge>
                      )}
                    </div>
                    {row.description && (
                      <div className="text-muted mt-1">{row.description}</div>
                    )}
                  </td>
                  <td className="small align-middle">
                    <a
                      href={row.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-truncate d-inline-block align-bottom"
                      style={{ maxWidth: 420 }}
                      title={row.url}
                    >
                      {row.url}
                    </a>
                  </td>
                  <td className="small align-middle text-muted">
                    {isOwned(row) ? (
                      "This org"
                    ) : (
                      <Badge bg="light" text="dark" className="border px-2 fw-normal">
                        {row.organization_name ?? "Platform-wide"}
                      </Badge>
                    )}
                  </td>
                  <td className="align-middle text-end">
                    {!isOwned(row) ? (
                      // Says why the row has no controls — an empty cell reads as a bug.
                      <span className="small text-muted fst-italic">Inherited</span>
                    ) : (
                      <div className="d-flex align-items-center justify-content-end gap-1">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => setFormModal({ approvedUrl: row })}
                          disabled={isSaving}
                          title="Edit"
                          style={ICON_BUTTON}
                        >
                          <i className="bi bi-pencil" />
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => setMoveTarget(row)}
                          disabled={isSaving}
                          title="Move to another organization"
                          style={ICON_BUTTON}
                        >
                          <i className="bi bi-arrow-left-right" />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => setDeleteTarget(row)}
                          disabled={isSaving}
                          title="Delete"
                          style={ICON_BUTTON}
                        >
                          <i className="bi bi-trash" />
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {formModal && (
        <ApprovedUrlFormModal
          show
          approvedUrl={formModal.approvedUrl}
          existingUrls={otherUrls(formModal.approvedUrl)}
          isSaving={isSaving}
          onClose={() => setFormModal(null)}
          onSave={handleSave}
        />
      )}

      <MoveApprovedUrlModal
        show={moveTarget !== null}
        approvedUrl={moveTarget}
        currentOrganizationId={organizationId}
        isSaving={isSaving}
        onClose={() => setMoveTarget(null)}
        onMoveConfirmed={handleMove}
      />

      <ModalDelete
        title="Approved URL"
        objectLabel={deleteTarget?.title ?? ""}
        deleteFn={handleDelete}
        visible={deleteTarget !== null}
        toggleVisibility={(visible: boolean) => {
          if (!visible) setDeleteTarget(null);
        }}
        isLoading={isSaving}
      />
    </div>
  );
};

export default OrganizationApprovedUrlsTab;
